import {
    VStack,
    Stack,
    Text,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    useToast,
    Box,
    Flex,
    Collapse,
} from '@chakra-ui/react';
import { Button, Dropdown, SegmentedControl } from '../../ui';
import { useLayout } from '../../hooks/useLayout';
import AppDrawer from '../../components/AppDrawer';
import { useState, useEffect, useRef, useMemo, useCallback, useContext } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import SessionContext from '../../contexts/SessionContext';
import { useCreateInventoryItem } from '../../hooks/inventory/useCreateInventoryItem';
import { useInventoryQuickAddPreview } from '../../hooks/inventory/useInventoryQuickAddPreview';
import { useGetInventoryUnits } from '../../hooks/inventory/useGetInventoryUnits';
import { useSearchSuppliers } from '../../hooks/useSearchSuppliers';
import {
    InventoryBin,
    InventoryCategory,
    CreateInventoryItemInput,
    InventoryFormReferenceField,
    InventoryTag,
    InventoryPartType,
    InventoryUnit,
} from '../../types/inventory';
import {
    INVENTORY_CATEGORY_OPTIONS,
    INVENTORY_PART_TYPE_OPTIONS,
    parseInventoryDuplicateError,
    parseInventoryPartType,
    parseInventoryReferenceValidationError,
    referenceValidationFieldToFormKey,
    coerceInventoryUnit,
} from '../../utils/inventoryConstants';
import { inventoryFieldProps } from '../../utils/inventoryFieldStyles';
import UnitSelectField from '../../components/inventory/UnitSelectField';
import CurrencyInput from '../../components/inventory/CurrencyInput';
import InventoryQuickAddLoadingBanner from '../../components/inventory/InventoryQuickAddLoadingBanner';
import type { TypeaheadOption } from '../../components/inventory/InventoryTypeaheadField';
import InventorySupplierDropdown from '../../components/inventory/InventorySupplierDropdown';
import QuantityInputField from '../../components/inventory/QuantityInputField';
import InventoryItemImageField from '../../components/inventory/InventoryItemImageField';
import InventoryTagsField from '../../components/inventory/InventoryTagsField';
import InventoryBinsField from '../../components/inventory/InventoryBinsField';
import InventoryAdditionalPartNumbersField from '../../components/inventory/InventoryAdditionalPartNumbersField';
import RoNumberField from '../../components/inventory/RoNumberField';
import { useUploadInventoryImage } from '../../hooks/inventory/useUploadInventoryImage';
import { useCreateInventoryTag } from '../../hooks/inventory/useCreateInventoryTag';
import { useCreateInventoryBin } from '../../hooks/inventory/useCreateInventoryBin';
import { filterLocations, LocationEntry } from '../../hooks/useGetLocations';
import { formatPartNumber } from '../../functions/formatPartNumber';
import ConfirmMakeTagModal from './ConfirmMakeTagModal';
import { appendTagIfMissing, tagListHasName } from '../../utils/inventoryTags';

const SectionHead = ({
    title,
    hint,
}: {
    title: string;
    hint?: string;
}) => (
    <Flex align="baseline" gap={2} mb={3} flexWrap="wrap">
        <Text
            fontSize="sm"
            fontWeight="bold"
            letterSpacing="0.06em"
            textTransform="uppercase"
            color="fg.default"
        >
            {title}
        </Text>
        {hint && (
            <Text fontSize="xs" color="gray.400" fontWeight="medium">
                ({hint})
            </Text>
        )}
    </Flex>
);

const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <FormLabel
        fontSize="sm"
        fontWeight="semibold"
        color="fg.muted"
        mb={1.5}
        requiredIndicator={
            <Text as="span" color="warning.fg" ml={0.5} aria-hidden>
                *
            </Text>
        }
    >
        {children}
    </FormLabel>
);

interface AddInventoryItemDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    locations: LocationEntry[];
    defaultLocationId?: string;
    onViewExistingItem: (inventoryItemId: string) => void;
    /** Opens the inventory import upload → mapping → preview flow. */
    onImport?: () => void;
}

interface FormState {
    manufacturerPartNumber: string;
    name: string;
    category: InventoryCategory;
    partType: InventoryPartType;
    supplierId: string;
    unit: InventoryUnit;
    restockAlert: boolean;
    reorderQuantity: number | null;
    targetQuantity: number | null;
    inStockQuantity: number;
    unitCost: number;
    locationId: string;
    partTerminologyId: number | null;
    origin: string;
}

const emptyForm = (locationId = ''): FormState => ({
    manufacturerPartNumber: '',
    name: '',
    category: 'part',
    partType: 'oem',
    supplierId: '',
    unit: 'each',
    restockAlert: false,
    reorderQuantity: null,
    targetQuantity: null,
    inStockQuantity: 0,
    unitCost: 0,
    locationId,
    partTerminologyId: null,
    origin: '',
});

const AddInventoryItemDrawer = ({
    isOpen,
    onClose,
    locations,
    defaultLocationId = '',
    onViewExistingItem,
    onImport,
}: AddInventoryItemDrawerProps) => {
    const toast = useToast();
    const layout = useLayout();
    const queryClient = useQueryClient();
    const sessionContext = useContext(SessionContext);
    const organizationName =
        sessionContext?.isAuthenticated === true
            ? sessionContext.organizationName
            : null;
    const abortRef = useRef<AbortController | null>(null);
    const [previewMatchItemId, setPreviewMatchItemId] = useState<string | null>(null);
    const [compositeDuplicateItemId, setCompositeDuplicateItemId] = useState<string | null>(
        null,
    );
    const [fieldErrors, setFieldErrors] = useState<
        Partial<Record<InventoryFormReferenceField, string>>
    >({});
    const [form, setForm] = useState<FormState>(() => emptyForm(defaultLocationId));
    const [additionalPartNumbers, setAdditionalPartNumbers] = useState<Array<string>>([]);
    const [manualModeEntered, setManualModeEntered] = useState(false);
    const [lookupStarted, setLookupStarted] = useState(false);
    const [showLookupButtonAgain, setShowLookupButtonAgain] = useState(false);
    const [lastCommittedMfgPartNumber, setLastCommittedMfgPartNumber] = useState('');
    const [lastCommittedCategory, setLastCommittedCategory] = useState<InventoryCategory | ''>('');
    const [supplierSearch, setSupplierSearch] = useState('');
    const [tags, setTags] = useState<Array<InventoryTag>>([]);
    const [suggestedMake, setSuggestedMake] = useState<string | null>(null);
    const [makeTagModalOpen, setMakeTagModalOpen] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [bins, setBins] = useState<Array<InventoryBin>>([]);
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
    const [imageKey, setImageKey] = useState<string | undefined>(undefined);
    const [photoAndTagsOpen, setPhotoAndTagsOpen] = useState(false);
    const uploadImageMutation = useUploadInventoryImage();
    const createTagMutation = useCreateInventoryTag();
    const createBinMutation = useCreateInventoryBin();

    const { data: allSuppliersData, isLoading: isSupplierLoading } = useSearchSuppliers('', {
        listAll: isOpen,
    });

    const {
        data: unitsData,
        isLoading: isUnitsLoading,
        isError: isUnitsError,
    } = useGetInventoryUnits(isOpen);

    const allowedUnits = unitsData?.units ?? [];

    const supplierOptions: TypeaheadOption[] = useMemo(() => {
        const all = allSuppliersData?.suppliers ?? [];
        const q = supplierSearch.trim().toLowerCase();
        return all
            .filter((s) => !q || s.title.toLowerCase().includes(q))
            .map((s) => ({ value: s._id, label: s.title }));
    }, [allSuppliersData?.suppliers, supplierSearch]);

    const supplierDisplay = useMemo(() => {
        const match = supplierOptions.find((o) => o.value === form.supplierId);
        if (match) return match.label;
        return supplierSearch;
    }, [supplierOptions, form.supplierId, supplierSearch]);

    const selectableLocations = useMemo(
        () => filterLocations(locations, { excludeAll: true }),
        [locations],
    );

    const locationOptions: TypeaheadOption[] = useMemo(
        () => selectableLocations.map((loc) => ({ value: loc._id, label: loc.title })),
        [selectableLocations],
    );

    const handleCreateError = useCallback((error: unknown) => {
        const referenceError = parseInventoryReferenceValidationError(error);
        if (referenceError) {
            const formKey = referenceValidationFieldToFormKey(referenceError.field);
            setFieldErrors({ [formKey]: referenceError.message });
            return;
        }
        const duplicate = parseInventoryDuplicateError(error);
        if (duplicate) {
            setCompositeDuplicateItemId(duplicate.existingItemId);
            return;
        }
        const message = (error as { response?: { data?: { message?: string; }; }; })?.response
            ?.data?.message;
        toast({
            title: 'Failed to add part',
            description: message ?? 'Please try again.',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    }, [toast]);

    const createMutation = useCreateInventoryItem({
        onError: handleCreateError,
    });

    const previewMutation = useInventoryQuickAddPreview({
        onSuccess: (data, variables) => {
            setLookupStarted(false);
            const s = data.suggestions;
            const committedMfg = formatPartNumber(data.manufacturerPartNumber);
            setForm((prev) => ({
                ...prev,
                manufacturerPartNumber: committedMfg,
                name: s.name ?? prev.name,
                partType: s.partType
                    ? parseInventoryPartType(s.partType)
                    : prev.partType,
                unit: coerceInventoryUnit(s.unit, allowedUnits),
                supplierId: s.supplierId ?? prev.supplierId,
                unitCost: s.averageUnitCost ?? s.unitCost ?? prev.unitCost,
                partTerminologyId:
                    variables.category === 'part' && s.partTerminologyId != null
                        ? s.partTerminologyId
                        : null,
            }));
            if (s.supplierName) {
                setSupplierSearch(s.supplierName);
            }
            const suggestionEquivalents = (s.equivalentPartNumbers ?? [])
                .map((v) => formatPartNumber(v))
                .filter((v) => v && v !== committedMfg);
            const uniqueExtras: string[] = [];
            const seenExtras = new Set<string>();
            for (const v of suggestionEquivalents) {
                if (seenExtras.has(v)) continue;
                seenExtras.add(v);
                uniqueExtras.push(v);
            }
            if (uniqueExtras.length > 0) {
                setAdditionalPartNumbers(uniqueExtras);
            }
            setManualModeEntered(true);
            setLastCommittedMfgPartNumber(committedMfg);
            setLastCommittedCategory(variables.category);
            setShowLookupButtonAgain(false);
            setPreviewMatchItemId(data.existingItemId ?? null);
            const make =
                variables.category === 'part' && s.suggestedMake?.trim()
                    ? s.suggestedMake.trim()
                    : null;
            setSuggestedMake(make);
            if (make) {
                setTags((prev) => appendTagIfMissing(prev, make));
            }
        },
        onError: (error: any) => {
            if (error?.code === 'ERR_CANCELED') return;
            setLookupStarted(false);
            toast({
                title: 'Lookup failed',
                description:
                    error?.response?.data?.message ?? 'Please try again or enter details manually.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            setManualModeEntered(true);
            setLastCommittedMfgPartNumber(formatPartNumber(form.manufacturerPartNumber));
            setLastCommittedCategory(form.category);
            setShowLookupButtonAgain(false);
            setSuggestedMake(null);
        },
    });

    const resetDrawerState = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
        setFieldErrors({});
        setManualModeEntered(false);
        setLookupStarted(false);
        setShowLookupButtonAgain(false);
        setLastCommittedMfgPartNumber('');
        setLastCommittedCategory('');
        setSupplierSearch('');
        setTags([]);
        setSuggestedMake(null);
        setMakeTagModalOpen(false);
        setIsSaving(false);
        setBins([]);
        setImagePreviewUrl((prev) => {
            if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev);
            return null;
        });
        setImageKey(undefined);
        setPhotoAndTagsOpen(false);
        previewMutation.reset();
        setForm(emptyForm(defaultLocationId));
        setAdditionalPartNumbers([]);
    }, [defaultLocationId, previewMutation]);

    useEffect(() => {
        if (!isOpen) return;
        queryClient.invalidateQueries({ queryKey: ['/locations/find'] });
        resetDrawerState();
        // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when drawer opens
    }, [isOpen]);

    const handleClose = () => {
        resetDrawerState();
        onClose();
    };

    const handleImportClick = () => {
        handleClose();
        onImport?.();
    };

    const handleLookup = useCallback(() => {
        const primary = formatPartNumber(form.manufacturerPartNumber);
        if (!primary) return;
        setManualModeEntered(false);
        setLookupStarted(true);
        setShowLookupButtonAgain(false);
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
        setSuggestedMake(null);
        abortRef.current?.abort();
        abortRef.current = new AbortController();
        previewMutation.mutate({
            manufacturerPartNumber: primary,
            category: form.category,
            signal: abortRef.current.signal,
        });
    }, [form.manufacturerPartNumber, form.category, previewMutation]);

    const handleSkip = () => {
        abortRef.current?.abort();
        abortRef.current = null;
        previewMutation.reset();
        setLookupStarted(false);
        setManualModeEntered(true);
        setLastCommittedMfgPartNumber(formatPartNumber(form.manufacturerPartNumber));
        setLastCommittedCategory(form.category);
        setShowLookupButtonAgain(false);
        setSuggestedMake(null);
    };

    const handleClear = () => {
        const mfg = form.manufacturerPartNumber;
        const category = form.category;
        setForm({
            ...emptyForm(form.locationId),
            manufacturerPartNumber: mfg,
            category,
            partTerminologyId: null,
        });
        setAdditionalPartNumbers([]);
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
        setManualModeEntered(false);
        setLookupStarted(false);
        setShowLookupButtonAgain(false);
        setLastCommittedMfgPartNumber('');
        setLastCommittedCategory('');
        setSuggestedMake(null);
        previewMutation.reset();
    };

    const isLoading = previewMutation.isPending;
    const showLookupButton =
        !isLoading &&
        ((!lookupStarted && !manualModeEntered) || showLookupButtonAgain);

    const submitCreate = async (tagsToSave: Array<InventoryTag> = tags) => {
        setIsSaving(true);
        const stockQty = form.inStockQuantity;

        try {
            const resolvedTagIds: string[] = [];
            for (const t of tagsToSave) {
                if (t.isNew) {
                    const { tag } = await createTagMutation.mutateAsync({
                        name: t.name,
                    });
                    resolvedTagIds.push(tag._id);
                } else {
                    resolvedTagIds.push(t._id);
                }
            }

            const resolvedBinIds: string[] = [];
            for (const b of bins) {
                if (b.isNew) {
                    const { bin } = await createBinMutation.mutateAsync({
                        locationId: form.locationId,
                        name: b.name,
                    });
                    resolvedBinIds.push(bin._id);
                } else {
                    resolvedBinIds.push(b._id);
                }
            }

            const primary = formatPartNumber(form.manufacturerPartNumber);
            if (!primary) {
                toast({
                    title: 'Missing required fields',
                    description: 'Mfg. part number and name are required.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
            const seen = new Set<string>([primary]);
            const equivalentPartNumbers: string[] = [primary];
            for (const raw of additionalPartNumbers) {
                const value = formatPartNumber(raw);
                if (!value || seen.has(value)) continue;
                seen.add(value);
                equivalentPartNumbers.push(value);
            }

            const payload: CreateInventoryItemInput = {
                locationId: form.locationId,
                manufacturerPartNumber: primary,
                equivalentPartNumbers,
                name: form.name.trim(),
                category: form.category,
                partType: form.partType,
                supplierId: form.supplierId,
                unit: form.unit,
                restockAlert: form.restockAlert,
                reorderQuantity: form.restockAlert ? (form.reorderQuantity ?? 0) : form.reorderQuantity,
                targetQuantity: form.restockAlert ? (form.targetQuantity ?? 0) : form.targetQuantity,
                initialInStockQuantity: stockQty,
                ...(form.origin.trim() ? { origin: form.origin.trim() } : {}),
                ...(stockQty > 0 ? { unitCost: form.unitCost } : {}),
                ...(form.category === 'part' && form.partTerminologyId != null
                    ? { partTerminologyId: form.partTerminologyId }
                    : {}),
                ...(imageKey ? { imageKey } : {}),
                ...(resolvedTagIds.length > 0 ? { tagIds: resolvedTagIds } : {}),
                ...(resolvedBinIds.length > 0 ? { binIds: resolvedBinIds } : {}),
            };

            const { item } = await createMutation.mutateAsync(payload);

            toast({
                title: 'Item added to inventory',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            queryClient.invalidateQueries({ queryKey: ['/inventory/feed'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/summary'] });
            queryClient.invalidateQueries({ queryKey: ['/inventory/activity-logs'] });
            queryClient.invalidateQueries({
                queryKey: ['/inventory/adjustments/feed', item._id],
            });
            handleClose();
        } catch {
            // create errors handled by handleCreateError
        } finally {
            setIsSaving(false);
        }
    };

    const handleSave = () => {
        if (!formatPartNumber(form.manufacturerPartNumber) || !form.name.trim()) {
            toast({
                title: 'Missing required fields',
                description: 'Mfg. part number and name are required.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }
        if (!form.locationId || !selectableLocations.some((l) => l._id === form.locationId)) {
            setFieldErrors((prev) => ({
                ...prev,
                locationId: form.locationId ? 'Location not found.' : 'Location is required.',
            }));
            return;
        }

        if (!form.supplierId) {
            setFieldErrors((prev) => ({
                ...prev,
                supplierId: 'Select a supplier from the list.',
            }));
            toast({
                title: 'Supplier required',
                description: 'Select a supplier from the list.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        if (compositeDuplicateItemId) return;

        if (form.restockAlert) {
            if (form.reorderQuantity == null || form.targetQuantity == null) {
                toast({
                    title: 'Restock quantities required',
                    description:
                        'Reorder and target quantities are required when restock alert is on.',
                    status: 'warning',
                    duration: 4000,
                    isClosable: true,
                });
                return;
            }
        }

        if (form.category !== 'part') {
            submitCreate();
            return;
        }

        const make = suggestedMake?.trim() || null;
        if (make && tagListHasName(tags, make)) {
            submitCreate();
            return;
        }
        setMakeTagModalOpen(true);
    };

    const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
        setForm((prev) => {
            if (key === 'locationId' && prev.locationId !== value) {
                setBins([]);
            }
            return { ...prev, [key]: value };
        });
        if (key === 'locationId' || key === 'supplierId' || key === 'unit') {
            setCompositeDuplicateItemId(null);
            const formKey = key as InventoryFormReferenceField;
            setFieldErrors((prev) => {
                if (!prev[formKey]) return prev;
                const next = { ...prev };
                delete next[formKey];
                return next;
            });
        }
    };

    const handleMfgPartNumberChange = (value: string) => {
        const stripped = formatPartNumber(value);
        setForm((prev) => ({
            ...prev,
            manufacturerPartNumber: value,
            ...(lastCommittedMfgPartNumber !== '' && stripped !== lastCommittedMfgPartNumber
                ? { partTerminologyId: null }
                : {}),
        }));
        setPreviewMatchItemId(null);
        if (stripped) {
            setAdditionalPartNumbers((prev) =>
                prev.filter((pn) => formatPartNumber(pn) !== stripped),
            );
        }
        if (
            manualModeEntered &&
            lastCommittedMfgPartNumber !== '' &&
            stripped !== lastCommittedMfgPartNumber
        ) {
            setShowLookupButtonAgain(true);
        }
    };

    const handleCategoryChange = (value: InventoryCategory) => {
        setForm((prev) => ({
            ...prev,
            category: value,
            partTerminologyId: null,
        }));
        setPreviewMatchItemId(null);
        setCompositeDuplicateItemId(null);
        if (value !== 'part') {
            setSuggestedMake(null);
        }
        if (
            manualModeEntered &&
            lastCommittedCategory !== '' &&
            value !== lastCommittedCategory
        ) {
            setShowLookupButtonAgain(true);
        }
    };

    const remainingRequiredCount = useMemo(() => {
        if (!manualModeEntered) return 0;
        let missing = 0;
        if (!form.category) missing += 1;
        if (!formatPartNumber(form.manufacturerPartNumber)) missing += 1;
        if (!form.name.trim()) missing += 1;
        if (!form.partType) missing += 1;
        if (!form.supplierId) missing += 1;
        if (!form.locationId) missing += 1;
        if (form.inStockQuantity == null || Number.isNaN(form.inStockQuantity)) missing += 1;
        if (!form.unit) missing += 1;
        if (form.restockAlert) {
            if (form.reorderQuantity == null) missing += 1;
            if (form.targetQuantity == null) missing += 1;
        }
        return missing;
    }, [manualModeEntered, form]);

    const footerStatus =
        remainingRequiredCount === 0 ? (
            <Text fontSize="sm" color="fg.muted">
                All required fields complete
            </Text>
        ) : (
            <Text fontSize="sm" color="fg.muted">
                <Text as="span" fontWeight="bold" color="fg.default">
                    {remainingRequiredCount}
                </Text>{' '}
                required field{remainingRequiredCount === 1 ? '' : 's'} left
            </Text>
        );

    const infoBanner = (message: string, itemId: string) => (
        <Flex
            align="center"
            gap={3}
            bg="info.subtle"
            borderWidth="1px"
            borderColor="brand.muted"
            color="info.fg"
            borderRadius="md"
            px={3}
            py={2.5}
            fontSize="sm"
            mt={3.5}
        >
            <Text flex={1}>{message}</Text>
            <Button
                size="sm"
                variant="outline"
                flexShrink={0}
                onClick={() => onViewExistingItem(itemId)}
            >
                View Item
            </Button>
        </Flex>
    );

    return (
        <>
        <AppDrawer
            isOpen={isOpen}
            onClose={handleClose}
            title="Add Item"
            subtitle={
                organizationName ? `${organizationName} · Inventory` : 'Inventory'
            }
            size={layout === 'mobile' ? 'full' : 'md'}
            bodyProps={{ px: 5, py: 0 }}
            headerRight={
                onImport ? (
                    <Button size="sm" variant="outline" onClick={handleImportClick}>
                        Import From File
                    </Button>
                ) : undefined
            }
            footerLeft={manualModeEntered ? footerStatus : undefined}
            footer={
                manualModeEntered ? (
                    <>
                        <Button size="action" variant="outlineGray" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            size="action"
                            onClick={handleSave}
                            isLoading={
                                isSaving ||
                                createMutation.isPending ||
                                createTagMutation.isPending ||
                                createBinMutation.isPending
                            }
                            loadingText="Adding…"
                            isDisabled={
                                isSaving ||
                                !!compositeDuplicateItemId ||
                                isUnitsLoading ||
                                isUnitsError ||
                                allowedUnits.length === 0 ||
                                uploadImageMutation.isPending
                            }
                        >
                            Add Item
                        </Button>
                    </>
                ) : undefined
            }
        >
            <VStack spacing={0} align="stretch" divider={undefined}>
                <Box py={4} borderBottomWidth="1px" borderColor="border.subtle">
                    <SectionHead
                        title="Identify The Part"
                        hint="We'll fill in what we can find"
                    />
                    <Stack
                        direction={{ base: 'column', sm: 'row' }}
                        spacing={3}
                        align={{ base: 'stretch', sm: 'flex-end' }}
                    >
                        <FormControl isRequired w={{ base: '100%', sm: '140px' }} flexShrink={0}>
                            <FieldLabel>Category</FieldLabel>
                            <Dropdown
                                size="sm"
                                options={INVENTORY_CATEGORY_OPTIONS.map((opt) => ({
                                    value: opt.value,
                                    label: opt.label,
                                }))}
                                value={form.category}
                                onChange={(value) => handleCategoryChange(value)}
                            />
                        </FormControl>
                        <FormControl isRequired flex={1}>
                            <Flex align="baseline" justify="space-between" gap={2}>
                                <FieldLabel>Mfg. Part Number</FieldLabel>
                                {manualModeEntered && !isLoading && (
                                    <Button
                                        variant="link"
                                        size="xs"
                                        fontWeight="semibold"
                                        flexShrink={0}
                                        onClick={handleClear}
                                    >
                                        Clear
                                    </Button>
                                )}
                            </Flex>
                            <Input
                                size="sm"
                                value={form.manufacturerPartNumber}
                                onChange={(e) => handleMfgPartNumberChange(e.target.value)}
                                onBlur={() => {
                                    const stripped = formatPartNumber(
                                        form.manufacturerPartNumber,
                                    );
                                    if (stripped !== form.manufacturerPartNumber) {
                                        setForm((prev) => ({
                                            ...prev,
                                            manufacturerPartNumber: stripped,
                                        }));
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && showLookupButton) {
                                        e.preventDefault();
                                        handleLookup();
                                    }
                                }}
                                {...inventoryFieldProps}
                            />
                        </FormControl>
                        {showLookupButton && (
                            <Button
                                size="sm"
                                variant="outlineGray"
                                onClick={handleLookup}
                                isDisabled={!formatPartNumber(form.manufacturerPartNumber)}
                                mb="1px"
                                flexShrink={0}
                                alignSelf={{ base: 'flex-start', sm: 'flex-end' }}
                            >
                                Look Up
                            </Button>
                        )}
                    </Stack>

                    <InventoryQuickAddLoadingBanner
                        isLoading={isLoading}
                        onSkip={handleSkip}
                    />

                    {manualModeEntered &&
                        compositeDuplicateItemId &&
                        infoBanner(
                            'An item with the same location, supplier, and category already exists for this part number.',
                            compositeDuplicateItemId,
                        )}

                    {manualModeEntered &&
                        !compositeDuplicateItemId &&
                        previewMatchItemId &&
                        infoBanner(
                            'This part is already in inventory.',
                            previewMatchItemId,
                        )}
                </Box>

                {manualModeEntered && (
                    <>
                        <Box py={4} borderBottomWidth="1px" borderColor="border.subtle">
                            <SectionHead title="Part Details" />
                            <FormControl isRequired mb={3}>
                                <FieldLabel>Name</FieldLabel>
                                <Input
                                    size="sm"
                                    value={form.name}
                                    onChange={(e) => updateField('name', e.target.value)}
                                    {...inventoryFieldProps}
                                />
                            </FormControl>
                            <Box mb={3}>
                                <InventoryAdditionalPartNumbersField
                                    value={additionalPartNumbers}
                                    onChange={setAdditionalPartNumbers}
                                    primaryPartNumber={form.manufacturerPartNumber}
                                    label="Additional Part Numbers"
                                    labelProps={{
                                        fontSize: 'sm',
                                        fontWeight: 'semibold',
                                        color: 'fg.muted',
                                        mb: 1.5,
                                    }}
                                />
                            </Box>
                            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align="flex-start" mb={3}>
                                <FormControl isRequired flex={1} w="100%">
                                    <FieldLabel>Part Type</FieldLabel>
                                    <Dropdown
                                        size="sm"
                                        options={INVENTORY_PART_TYPE_OPTIONS.map((opt) => ({
                                            value: opt.value,
                                            label: opt.label,
                                        }))}
                                        value={form.partType}
                                        onChange={(value) =>
                                            updateField('partType', parseInventoryPartType(value))
                                        }
                                    />
                                </FormControl>
                                <FormControl
                                    isRequired
                                    flex={1}
                                    isInvalid={!!fieldErrors.supplierId}
                                >
                                    <FieldLabel>Supplier</FieldLabel>
                                    <InventorySupplierDropdown
                                        value={form.supplierId}
                                        displayLabel={supplierDisplay}
                                        options={supplierOptions}
                                        onSearch={setSupplierSearch}
                                        onSelect={(option) => {
                                            updateField('supplierId', option.value);
                                            setSupplierSearch(option.label);
                                        }}
                                        isLoading={isSupplierLoading}
                                    />
                                    <FormErrorMessage>
                                        {fieldErrors.supplierId}
                                    </FormErrorMessage>
                                </FormControl>
                            </Stack>
                            <FormControl>
                                <FieldLabel>
                                    Origin
                                    <Text
                                        as="span"
                                        color="gray.400"
                                        fontWeight="medium"
                                        fontSize="xs"
                                        ml={1}
                                    >
                                        (Original RO item was ordered for)
                                    </Text>
                                </FieldLabel>
                                <RoNumberField
                                    value={form.origin}
                                    onChange={(val) => updateField('origin', val)}
                                    placeholderNode={
                                        <Text as="span" fontSize="sm">
                                            Origin RO #{' '}
                                            <Text as="span" fontSize="xs">
                                                (optional)
                                            </Text>
                                        </Text>
                                    }
                                />
                            </FormControl>
                        </Box>

                        <Box py={4} borderBottomWidth="1px" borderColor="border.subtle">
                            <SectionHead
                                title="Where It Lives"
                                hint="Storage and count"
                            />
                            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align="flex-start" mb={3}>
                                <FormControl
                                    isRequired
                                    flex={1}
                                    w="100%"
                                    isInvalid={!!fieldErrors.locationId}
                                >
                                    <FieldLabel>Location</FieldLabel>
                                    <Dropdown
                                        size="sm"
                                        searchable
                                        options={locationOptions.map((option) => ({
                                            value: option.value,
                                            label: option.label,
                                        }))}
                                        value={form.locationId || null}
                                        onChange={(value) => updateField('locationId', value)}
                                        placeholder="Select location"
                                    />
                                    <FormErrorMessage>
                                        {fieldErrors.locationId}
                                    </FormErrorMessage>
                                </FormControl>
                                <Box flex={1} w="100%">
                                    <FieldLabel>Bin</FieldLabel>
                                    <InventoryBinsField
                                        size="sm"
                                        value={bins}
                                        onChange={setBins}
                                        locationId={form.locationId || null}
                                        label=""
                                        isDisabled={!form.locationId}
                                        placeholder={
                                            form.locationId
                                                ? 'Search bins'
                                                : 'Select a location first'
                                        }
                                    />
                                </Box>
                            </Stack>
                            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align="flex-start">
                                <FormControl isRequired flex={1} w="100%">
                                    <FieldLabel>In-Stock Quantity</FieldLabel>
                                    <QuantityInputField
                                        value={form.inStockQuantity}
                                        onChange={(val) =>
                                            updateField('inStockQuantity', val ?? 0)
                                        }
                                    />
                                </FormControl>
                                <FormControl
                                    isRequired
                                    flex={1}
                                    w="100%"
                                    isInvalid={!!fieldErrors.unit || isUnitsError}
                                >
                                    <FieldLabel>Unit</FieldLabel>
                                    <UnitSelectField
                                        value={form.unit}
                                        onChange={(val) => updateField('unit', val)}
                                        isDisabled={isUnitsLoading || isUnitsError}
                                    />
                                    {fieldErrors.unit && (
                                        <FormErrorMessage>{fieldErrors.unit}</FormErrorMessage>
                                    )}
                                </FormControl>
                            </Stack>
                        </Box>

                        <Box py={4} borderBottomWidth="1px" borderColor="border.subtle">
                            <SectionHead
                                title="Cost And Restocking"
                                hint="Turn restocking off to skip low-stock alerts"
                            />
                            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align="flex-start" mb={3}>
                                <FormControl flex={1} w="100%">
                                    <FieldLabel>Unit Cost</FieldLabel>
                                    <CurrencyInput
                                        value={form.unitCost}
                                        onChange={(val) => updateField('unitCost', val)}
                                        useThousandsSeparator
                                    />
                                </FormControl>
                                <FormControl flex={1} w="100%">
                                    <FieldLabel>Restock Alerts</FieldLabel>
                                    <SegmentedControl
                                        size="sm"
                                        fullWidth
                                        items={[
                                            { value: 'on', label: 'On' },
                                            { value: 'off', label: 'Off' },
                                        ]}
                                        value={form.restockAlert ? 'on' : 'off'}
                                        onChange={(next) => {
                                            const on = next === 'on';
                                            setForm((prev) => ({
                                                ...prev,
                                                restockAlert: on,
                                                ...(on
                                                    ? {
                                                          reorderQuantity:
                                                              prev.reorderQuantity ?? 0,
                                                          targetQuantity:
                                                              prev.targetQuantity ?? 0,
                                                      }
                                                    : {}),
                                            }));
                                        }}
                                    />
                                </FormControl>
                            </Stack>
                            {form.restockAlert && (
                                <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align="flex-start">
                                    <FormControl isRequired={form.restockAlert} flex={1} w="100%">
                                        <FieldLabel>
                                            Reorder At
                                        </FieldLabel>
                                        <QuantityInputField
                                            value={form.reorderQuantity}
                                            onChange={(val) =>
                                                updateField('reorderQuantity', val)
                                            }
                                            allowEmpty={!form.restockAlert}
                                        />
                                    </FormControl>
                                    <FormControl isRequired={form.restockAlert} flex={1} w="100%">
                                        <FieldLabel>
                                            Restock To
                                        </FieldLabel>
                                        <QuantityInputField
                                            value={form.targetQuantity}
                                            onChange={(val) =>
                                                updateField('targetQuantity', val)
                                            }
                                            allowEmpty={!form.restockAlert}
                                        />
                                    </FormControl>
                                </Stack>
                            )}
                        </Box>

                        <Box py={4}>
                            <Flex
                                as="button"
                                type="button"
                                align="center"
                                gap={2}
                                cursor="pointer"
                                onClick={() => setPhotoAndTagsOpen((prev) => !prev)}
                                w="100%"
                                textAlign="left"
                            >
                                <Text
                                    fontSize="sm"
                                    color="fg.muted"
                                    w="14px"
                                    flexShrink={0}
                                    fontWeight="bold"
                                >
                                    {photoAndTagsOpen ? '–' : '+'}
                                </Text>
                                <Text
                                    fontSize="sm"
                                    fontWeight="bold"
                                    letterSpacing="0.06em"
                                    textTransform="uppercase"
                                    color="fg.muted"
                                >
                                    Photo And Tags
                                </Text>
                            </Flex>
                            <Collapse in={photoAndTagsOpen} animateOpacity>
                                <Box pt={3.5}>
                                    <InventoryTagsField
                                        value={tags}
                                        onChange={setTags}
                                    />
                                    <Box mt={3}>
                                        <InventoryItemImageField
                                            fullWidth
                                            previewUrl={imagePreviewUrl}
                                            alt={form.name}
                                            isUploading={uploadImageMutation.isPending}
                                            cropTitle={`${form.name.trim() || '—'} · ${formatPartNumber(form.manufacturerPartNumber) || '—'}`}
                                            onFileSelected={async (file) => {
                                                const objectUrl = URL.createObjectURL(file);
                                                setImagePreviewUrl((prev) => {
                                                    if (prev?.startsWith('blob:')) {
                                                        URL.revokeObjectURL(prev);
                                                    }
                                                    return objectUrl;
                                                });
                                                try {
                                                    const key =
                                                        await uploadImageMutation.mutateAsync(file);
                                                    setImageKey(key);
                                                } catch {
                                                    setImagePreviewUrl((prev) => {
                                                        if (prev?.startsWith('blob:')) {
                                                            URL.revokeObjectURL(prev);
                                                        }
                                                        return null;
                                                    });
                                                    setImageKey(undefined);
                                                    toast({
                                                        title: 'Image upload failed',
                                                        status: 'error',
                                                        duration: 4000,
                                                        isClosable: true,
                                                    });
                                                }
                                            }}
                                            onClear={() => {
                                                setImagePreviewUrl((prev) => {
                                                    if (prev?.startsWith('blob:')) {
                                                        URL.revokeObjectURL(prev);
                                                    }
                                                    return null;
                                                });
                                                setImageKey(undefined);
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Collapse>
                        </Box>
                    </>
                )}
            </VStack>
        </AppDrawer>
        <ConfirmMakeTagModal
            isOpen={makeTagModalOpen}
            makeName={suggestedMake}
            onConfirm={(name) => {
                const next = appendTagIfMissing(tags, name);
                setTags(next);
                setMakeTagModalOpen(false);
                submitCreate(next);
            }}
            onCancel={() => setMakeTagModalOpen(false)}
        />
        </>
    );
};

export default AddInventoryItemDrawer;
